import { useEffect, useState } from "react";

import {
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import KsbDialogActions from "../../../../2_ksbHub/components/KsbDialogActions";
import { useBszPlannerContext } from "../../context/bszPlannerContext";
import { useAppContext } from "../../../../2_ksbHub/context/appContext";

export default function PlannerProjectDialog({
  open,
  onClose,
  onSave,
  onDelete,

  project = null,

  subjectName = "",
  week = null,
  selectedClass = null,
}) {
  const isEditMode = Boolean(project);

  const { currentUser, isAStudent } = useAppContext();

  const { subjects, isBszAdmin } = useBszPlannerContext();

  //only admins and project creators can edit / open new project
  const canEdit =
    !isAStudent &&
    (!project ||
      (isEditMode && project.teacherId === currentUser?.id) ||
      isBszAdmin);

  const subject = subjects.find(
    (s) => s.name === subjectName || s.shortName === subjectName,
  );

  const subjectId = subject?.id;

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");

  const [startDate, setStartDate] = useState("");
  const [dueDate, setDueDate] = useState("");

  const [estimatedMinutes, setEstimatedMinutes] = useState(60);

  const [evaluation, setEvaluation] = useState(true);

  const [error, setError] = useState("");

  /**
   * =========================================================
   * ✅ INIT
   * =========================================================
   */
  useEffect(() => {
    if (!open) return;

    if (project) {
      setTitle(project.title ?? "");
      setDescription(project.description ?? "");

      setStartDate(project.startDate ? project.startDate.slice(0, 10) : "");

      setDueDate(project.dueDate ? project.dueDate.slice(0, 10) : "");

      setEstimatedMinutes(project.estimatedMinutes || 60);

      setEvaluation(project.evaluation ?? true);
    } else {
      setTitle("");
      setDescription("");

      const defaultDate = week ? week.format("YYYY-MM-DD") : "";

      setStartDate(defaultDate);
      setDueDate(defaultDate);

      setEstimatedMinutes(60);
      setEvaluation(true);
    }

    setError("");
  }, [open, project, week]);

  /**
   * =========================================================
   * ✅ SAVE
   * =========================================================
   */
  const handleSave = async () => {
    if (!canEdit) {
      setError("Dieses Projekt kann von dir nicht bearbeitet werden.");
      return;
    }
    if (!title.trim()) {
      setError("Bitte einen Titel eingeben.");
      return;
    }

    try {
      setError("");
      if (!selectedClass?.id) {
        setError("Bitte zuerst eine Klasse auswählen.");
        return;
      }
      await onSave({
        projectData: {
          id: project?.id,

          title,
          description,

          startDate,
          dueDate,

          estimatedMinutes: Number(estimatedMinutes),

          evaluation,

          subjectId,
          classId: selectedClass?.id,
        },

        closeDialog: onClose,
      });
    } catch (err) {
      console.error(err);

      setError("Fehler beim Speichern des Projekts.");
    }
  };

  /**
   * =========================================================
   * ✅ DELETE
   * =========================================================
   */
  const handleDelete = async () => {
    if (!canEdit) {
      setError("Dieses Projekt kann von dir nicht gelöscht werden.");
      return;
    }
    if (!project?.id) {
      return;
    }

    try {
      await onDelete({ project, closeDialog: onClose });
    } catch (err) {
      console.error(err);

      setError("Fehler beim Löschen des Projekts.");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {isAStudent
          ? "Projekt Information"
          : isEditMode
            ? "Projekt bearbeiten"
            : "Neues Projekt"}
      </DialogTitle>

      <DialogContent>
        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        <Stack spacing={2} mt={1}>
          <TextField label="Fach" value={subjectName ?? ""} disabled />

          <TextField
            label="Klasse"
            value={selectedClass?.name ?? "keine"}
            disabled
          />

          <TextField
            label="Titel"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            disabled={!canEdit}
          />

          <TextField
            label="Beschreibung"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            minRows={3}
            fullWidth
            disabled={!canEdit}
          />

          <TextField
            label="Startdatum"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
            disabled={!canEdit}
          />

          <TextField
            label="Deadline"
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
            disabled={!canEdit}
          />

          <TextField
            label="Geschätzter Aufwand (Minuten)"
            type="number"
            value={estimatedMinutes}
            onChange={(e) => setEstimatedMinutes(e.target.value)}
            disabled={!canEdit}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={evaluation}
                onChange={(e) => setEvaluation(e.target.checked)}
                disabled={!canEdit}
              />
            }
            label="Bewertet"
          />
        </Stack>
      </DialogContent>

      <KsbDialogActions
        onCancel={onClose}
        onConfirm={handleSave}
        confirmLabel={isEditMode ? "Speichern" : "Erstellen"}
        dangerAction={isEditMode ? handleDelete : undefined}
        dangerLabel={isEditMode ? "Löschen" : undefined}
        dangerConfirm={{
          title: "Projekt löschen?",
          message: "Dieses Projekt wird dauerhaft gelöscht.",
          confirmLabel: "Löschen",
          cancelLabel: "Abbrechen",
        }}
      />
    </Dialog>
  );
}
